<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import addMonths from 'date-fns/addMonths';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAlert } from 'dashboard/composables';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import PageHeader from 'dashboard/components-next/masifi/page-header/PageHeader.vue';
import LeadTasksAPI from 'dashboard/api/masifi/leadTasks';
import LeadTaskTypesAPI from 'dashboard/api/masifi/leadTaskTypes';
import LeadTaskDialog from '../../leads/components/LeadTaskDialog.vue';
import { unwrap, fetchAllTasks } from '../../leads/helper';
import AgendaBoard from '../components/AgendaBoard.vue';
import AgendaList from '../components/AgendaList.vue';
import AgendaCalendar from '../components/AgendaCalendar.vue';
import TaskTypesDialog from '../components/TaskTypesDialog.vue';

const route = useRoute();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const view = ref('board'); // 'board' | 'list' | 'calendar'
const preset = ref('mine');
const agentFilter = ref(''); // override de assignee (solo con acceso completo)
const monthCursor = ref(startOfMonth(new Date()));

const tasks = ref([]);
const isLoading = ref(false);
const types = ref([]);

// Paginación de la lista.
const page = ref(1);
const hasMore = ref(false);

const dialog = ref(null);
const typesDialog = ref(null);

const currentUserId = computed(() => store.getters.getCurrentUser?.id);
const agents = computed(() => store.getters['agents/getAgents'] || []);

// Acceso completo: admin o agente sin rol restrictivo (espejo de LeadsIndex/LeadPolicy).
const accountPermissions = computed(
  () =>
    getUserPermissions(
      store.getters.getCurrentUser,
      Number(route.params.accountId)
    ) || []
);
const hasFullAccess = computed(() => {
  const perms = accountPermissions.value;
  if (perms.includes('administrator') || perms.includes('conversation_manage'))
    return true;
  return !(
    perms.includes('conversation_participating_manage') ||
    perms.includes('conversation_unassigned_manage')
  );
});

const presets = computed(() => {
  const base = ['mine', 'overdue', 'today', 'completed'];
  return hasFullAccess.value ? [...base, 'team'] : base;
});

const agentOptions = computed(() => [
  { value: '', label: t('LEADS.AGENDA.ALL_AGENTS') },
  ...agents.value.map(a => ({ value: a.id, label: a.name })),
]);

// Para el form de tarea: "Sin asignar" + agentes (NO "Todos", que es solo para filtrar).
const assigneeFormOptions = computed(() => [
  { value: '', label: t('LEADS.TASK.UNASSIGNED') },
  ...agents.value.map(a => ({ value: a.id, label: a.name })),
]);

const showAssignee = computed(
  () =>
    hasFullAccess.value && (preset.value === 'team' || preset.value === 'all')
);

const typeNameById = computed(() =>
  Object.fromEntries(types.value.map(ty => [ty.id, ty.name]))
);
const agentNameById = computed(() =>
  Object.fromEntries(agents.value.map(a => [a.id, a.name]))
);
const resolveTypeName = task =>
  typeNameById.value[task.lead_task_type_id] || '';
const resolveAssigneeName = task => agentNameById.value[task.assignee_id] || '';

// Construye los params de assignee/scope según preset + filtro de agente.
const assigneeParam = () => {
  if (hasFullAccess.value) {
    if (preset.value === 'team' || preset.value === 'all')
      return agentFilter.value || '';
    return currentUserId.value; // mine/overdue/today/completed = propias
  }
  return ''; // restringido: policy_scope ya limita a lo suyo
};
const scopeParam = () => {
  if (preset.value === 'overdue') return 'overdue';
  if (preset.value === 'today') return 'today';
  if (preset.value === 'completed') return 'completed';
  return 'pending';
};

let loadToken = 0;

const loadBoardOrCalendar = async () => {
  loadToken += 1;
  const token = loadToken;
  isLoading.value = true;
  try {
    const params =
      view.value === 'calendar'
        ? {
            scope: 'pending',
            assignee_id: assigneeParam(),
            from: startOfMonth(monthCursor.value).toISOString(),
            to: endOfMonth(monthCursor.value).toISOString(),
          }
        : { scope: 'pending', assignee_id: assigneeParam() };
    const result = await fetchAllTasks(params);
    if (token !== loadToken) return;
    tasks.value = result;
  } catch (error) {
    if (token === loadToken) useAlert(t('LEADS.AGENDA.LOAD_ERROR'));
  } finally {
    if (token === loadToken) isLoading.value = false;
  }
};

const loadList = async (append = false) => {
  loadToken += 1;
  const token = loadToken;
  isLoading.value = true;
  try {
    const { data } = await LeadTasksAPI.get({
      scope: scopeParam(),
      assignee_id: assigneeParam(),
      page: page.value,
    });
    if (token !== loadToken) return;
    const batch = unwrap(data);
    tasks.value = append ? [...tasks.value, ...batch] : batch;
    hasMore.value = batch.length >= 50;
  } catch (error) {
    if (token === loadToken) useAlert(t('LEADS.AGENDA.LOAD_ERROR'));
  } finally {
    if (token === loadToken) isLoading.value = false;
  }
};

const reload = () => {
  page.value = 1;
  if (view.value === 'list') loadList(false);
  else loadBoardOrCalendar();
};

const loadMore = () => {
  page.value += 1;
  loadList(true);
};

const loadTypes = async () => {
  try {
    const { data } = await LeadTaskTypesAPI.get();
    types.value = unwrap(data);
  } catch (error) {
    types.value = [];
  }
};

onMounted(() => {
  // Si venimos de un widget del tablero (?preset=overdue|today), aplicarlo.
  if (route.query.preset) preset.value = route.query.preset;
  store.dispatch('agents/get');
  loadTypes();
  reload();
  if (currentUserId.value) {
    store.dispatch('agenda/fetchStats', { assignee_id: currentUserId.value });
  }
});

watch([view, preset, agentFilter, monthCursor, currentUserId], reload);

// Los presets temporales (vencidas/hoy/completadas) solo tienen sentido como lista;
// el tablero/calendario son la agenda de pendientes (mías/equipo/todas).
watch(preset, p => {
  if (['overdue', 'today', 'completed'].includes(p) && view.value !== 'list') {
    view.value = 'list';
  }
  if (p !== 'team' && p !== 'all') agentFilter.value = '';
});

// Acciones del diálogo compartido.
const openNew = () => dialog.value?.openNew(null);
const onEdit = task => dialog.value?.openEdit(task);
const onComplete = task => dialog.value?.openComplete(task);
const onRemove = task => dialog.value?.remove(task);
const openTypes = () => typesDialog.value?.open();

const prevMonth = () => {
  monthCursor.value = addMonths(monthCursor.value, -1);
};
const nextMonth = () => {
  monthCursor.value = addMonths(monthCursor.value, 1);
};
const todayMonth = () => {
  monthCursor.value = startOfMonth(new Date());
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden">
    <PageHeader>
      <template #title>
        <h1 class="text-xl font-semibold truncate text-n-slate-12">
          {{ t('LEADS.AGENDA.TITLE') }}
        </h1>
      </template>
      <template #actions>
        <Button
          v-if="isAdmin"
          faded
          slate
          icon="i-lucide-settings-2"
          :label="t('LEADS.AGENDA.MANAGE_TYPES')"
          @click="openTypes"
        />
        <Button
          blue
          icon="i-lucide-plus"
          :label="t('LEADS.AGENDA.NEW_TASK')"
          @click="openNew"
        />
      </template>
      <template #below>
        <div class="flex flex-wrap items-center justify-between gap-2">
          <!-- Presets -->
          <div class="flex flex-wrap items-center gap-1">
            <Button
              v-for="p in presets"
              :key="p"
              :faded="preset !== p"
              :slate="preset !== p"
              :blue="preset === p"
              sm
              :label="t(`LEADS.AGENDA.PRESET.${p.toUpperCase()}`)"
              @click="preset = p"
            />
          </div>

          <div class="flex items-center gap-2">
            <!-- Filtro de agente (oculto a restringidos) -->
            <Select
              v-if="hasFullAccess && (preset === 'team' || preset === 'all')"
              v-model="agentFilter"
              class="w-44"
              :options="agentOptions"
            />
            <!-- Switcher de vista -->
            <div class="flex items-center gap-0.5">
              <Button
                :faded="view !== 'board'"
                :blue="view === 'board'"
                slate
                sm
                icon="i-lucide-columns-3"
                @click="view = 'board'"
              />
              <Button
                :faded="view !== 'list'"
                :blue="view === 'list'"
                slate
                sm
                icon="i-lucide-list"
                @click="view = 'list'"
              />
              <Button
                :faded="view !== 'calendar'"
                :blue="view === 'calendar'"
                slate
                sm
                icon="i-lucide-calendar"
                @click="view = 'calendar'"
              />
            </div>
          </div>
        </div>
      </template>
    </PageHeader>

    <!-- Cuerpo -->
    <div class="flex flex-col flex-1 gap-4 p-4 overflow-auto">
      <div v-if="isLoading && !tasks.length" class="flex justify-center py-12">
        <Spinner />
      </div>
      <div
        v-else-if="!tasks.length"
        class="flex flex-col items-center justify-center gap-2 py-16 text-center"
      >
        <span class="i-lucide-calendar-check size-8 text-n-slate-9" />
        <p class="text-sm text-n-slate-10">{{ t('LEADS.AGENDA.EMPTY') }}</p>
      </div>
      <template v-else>
        <AgendaBoard
          v-if="view === 'board'"
          :tasks="tasks"
          :show-assignee="showAssignee"
          :resolve-type-name="resolveTypeName"
          :resolve-assignee-name="resolveAssigneeName"
          @edit="onEdit"
          @complete="onComplete"
          @remove="onRemove"
        />
        <AgendaList
          v-else-if="view === 'list'"
          :tasks="tasks"
          :has-more="hasMore"
          :is-loading="isLoading"
          :show-assignee="showAssignee"
          :resolve-type-name="resolveTypeName"
          :resolve-assignee-name="resolveAssigneeName"
          @edit="onEdit"
          @complete="onComplete"
          @remove="onRemove"
          @load-more="loadMore"
        />
        <AgendaCalendar
          v-else
          :month-cursor="monthCursor"
          :tasks="tasks"
          @prev="prevMonth"
          @next="nextMonth"
          @today="todayMonth"
          @edit="onEdit"
        />
      </template>
    </div>

    <LeadTaskDialog
      ref="dialog"
      lead-picker
      :assignee-options="assigneeFormOptions"
      @saved="reload"
      @completed="reload"
      @deleted="reload"
    />
    <TaskTypesDialog ref="typesDialog" />
  </div>
</template>
